import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useCallback, useState } from "react"
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form"

import { useCreateGlTransaction } from "../../hooks/useCreateGlTransaction"
import { type GlTransactionFormValues, glTransactionFormSchema } from "../../schemas/glTransactionForm"
import { toGlTransactionCreateInput } from "../../utils/glTransactionFormUtils"
import { GlTransactionDateSection } from "../GlTransactionDateSection"
import { RecurringSection } from "../RecurringSection"
import { TransactionLinesSection } from "../TransactionLinesSection"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormCheckbox } from "@/components/UiKit/FormControls/FormCheckbox"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import { DOCUMENT_UPLOAD_MIME_TYPES, DOCUMENT_UPLOAD_TYPES_LABEL } from "@/components/upload/constants"
import { MultiFileUpload } from "@/components/upload/MultiFileUpload/MultiFileUpload"
import { getFieldFile } from "@/components/upload/MultiFileUpload/utils"
import {
  FieldGroupEnum,
  GlTransactionRepeatByUnit,
  GlTransactionRowType,
  ReferenceTableName,
} from "@/graphql/codegen/graphql"
import { useMultiFileUpload } from "@/hooks/useMultiFileUpload"

interface AddGlTransactionDialogProps {
  onClose: () => void
  onSuccess?: () => void
}

const DEFAULT_GL_TRANSACTION_FORM_VALUES: GlTransactionFormValues = {
  transactionLines: [
    { type: GlTransactionRowType.Debit, glAccountId: null, amount: null },
    { type: GlTransactionRowType.Credit, glAccountId: null, amount: null },
  ],
  transactionDate: "",
  description: "",
  note: "",
  isRecurring: false,
  isExistingRecurring: false,
  repeatEndDate: "",
  repeatEvery: 1,
  repeatByUnit: GlTransactionRepeatByUnit.Month,
  documents: [],
}

export const AddGlTransactionDialog: FC<AddGlTransactionDialogProps> = ({ onClose, onSuccess }) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const { createGlTransaction, isCreating } = useCreateGlTransaction()
  const { statuses, progress, isUploading, uploadFiles } = useMultiFileUpload({
    referenceTableName: ReferenceTableName.GlTransaction,
  })

  const methods = useForm<GlTransactionFormValues>({
    resolver: zodResolver(glTransactionFormSchema),
    defaultValues: DEFAULT_GL_TRANSACTION_FORM_VALUES,
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting },
  } = methods
  const { fields: documentFields } = useFieldArray({ control, name: "documents" })

  const canSubmit = isValid && isDirty && !isSubmitting && !isCreating && !isUploading

  const isRecurring = useWatch({ control, name: "isRecurring" })

  const handleFormSubmit = useCallback(
    async (formValues: GlTransactionFormValues) => {
      const input = toGlTransactionCreateInput(formValues)
      const transactionIds = await createGlTransaction(input)
      if (!transactionIds) return

      const items = documentFields.map((field) => ({
        id: field.id,
        file: getFieldFile(field),
        fieldGroup: FieldGroupEnum.Miscellaneous,
      }))

      if (!items.length) {
        onSuccess?.()
        onClose()
        return
      }

      const { failures } = await uploadFiles(items, {
        referenceIds: transactionIds,
        totalFailureMessage: "Transaction saved, but no documents were attached. Try again.",
      })

      if (failures.length === items.length) {
        return
      }

      onSuccess?.()
      onClose()
    },
    [createGlTransaction, documentFields, uploadFiles, onSuccess, onClose]
  )

  const handleClose = useCallback(() => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  if (isCancelDialogOpen) {
    return <CancelDialog onClose={() => setIsCancelDialogOpen(false)} onConfirm={onClose} />
  }

  return (
    <Dialog open onClose={handleClose} maxWidth='lg' fullWidth data-testid='gl-transaction.form-dialog'>
      <DialogHeader title='Add General Ledger Entry' onClose={handleClose} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent className='flex flex-col gap-6 pt-4'>
            <TransactionLinesSection />

            <Box className='grid grid-cols-3 items-start gap-4'>
              <GlTransactionDateSection showRecurringLabel testIdPrefix='gl-transaction.form-dialog' />

              <Box className='pt-5'>
                <FormCheckbox
                  control={control}
                  name='isRecurring'
                  label='Recurring Event'
                  data-testid='gl-transaction.form-dialog.recurring-checkbox'
                />
              </Box>
            </Box>

            {isRecurring && <RecurringSection />}

            <FormTextField
              control={control}
              name='description'
              label='Description'
              required
              slotProps={{ htmlInput: { maxLength: 30 } }}
              data-testid='gl-transaction.form-dialog.description'
            />

            <FormTextField
              control={control}
              name='note'
              label='Notes'
              multiline
              rows={3}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              data-testid='gl-transaction.form-dialog.note'
            />

            <MultiFileUpload
              control={control}
              name='documents'
              allowedMimeTypes={DOCUMENT_UPLOAD_MIME_TYPES}
              allowedTypesLabel={DOCUMENT_UPLOAD_TYPES_LABEL}
              statuses={statuses}
              progress={progress}
              isUploading={isUploading}
              testIdPrefix='gl-transaction.form-dialog.multi-file-upload'
            />
          </DialogContent>

          <DialogActions className='px-6 pb-6'>
            <Button appearance='outlined' onClick={handleClose} data-testid='gl-transaction.form-dialog.cancel-button'>
              Cancel
            </Button>
            <Button
              type='submit'
              appearance='contained'
              disabled={!canSubmit}
              data-testid='gl-transaction.form-dialog.save-button'
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
